/**
 * Layout Component
 * 
 * Main layout component that provides the overall structure for the application.
 * Includes sidebar navigation, header with user info, and main content area.
 * Serves as the template for all authenticated pages in the application.
 */

import React from 'react';
import type { User, NavigationTab } from '../../types';
import Sidebar from './Sidebar.tsx';
import Header from './Header.tsx';

/**
 * Props interface for Layout component
 */
interface LayoutProps {
  children: React.ReactNode;                         // Main content to render
  user: User;                                        // Current user information
  onTabChange: (tab: NavigationTab) => void;         // Handler for navigation tab changes
  onLogout?: () => void;                             // Optional logout handler
}

/**
 * Layout Component
 * 
 * Provides the main application layout structure with sidebar navigation,
 * header, and content area. Manages the overall page layout and passes
 * necessary props to child components.
 * 
 * @param children - Main content components to render
 * @param user - Current user data for header display
 * @param onTabChange - Function to handle navigation tab changes
 * @param onLogout - Optional function to handle user logout
 * @returns JSX element containing the complete layout structure
 */
const Layout: React.FC<LayoutProps> = ({ children, user, onTabChange, onLogout }) => {
  return (
    <div className="min-h-screen min-w-0 flex bg-gray-50">
      {/* Single responsive sidebar navigation */}
      <div className="sticky top-0 z-50 h-screen w-20 shrink-0 lg:z-30 lg:w-auto">
      <Sidebar onTabChange={onTabChange} onLogout={onLogout} />
      </div>
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
      {/* Header with user information - sticky */}
        <div className="sticky top-0 z-40 bg-white shadow-sm">
          <Header user={user} onTabChange={onTabChange} />
        </div>
      
      {/* Scrollable main content */}
      <main className="flex-1 overflow-y-auto">
        <div className="w-full min-w-0 mx-auto h-full bg-white px-3 py-4 sm:px-5 sm:py-5 lg:px-7 lg:py-6">
        {children}
        </div>
      </main>
      </div>
    </div>
  );
};

export default Layout;
